import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from 'api';
import type { SaveProgramQuestionRequest } from 'types';

export const programQuestionKeys = {
    questions: (
        country: string,
        userId?: number,
        form = 'program-question',
        programId?: string | null
    ) =>
        [
            'program-question',
            'questions',
            country,
            form,
            programId || null,
            userId ?? null,
        ] as const,
    applicable: (userId?: number) => ['program-question', 'applicable', userId ?? null] as const,
};

export const useProgramQuestions = (
    country: string,
    userId?: number,
    form = 'program-question',
    programId?: string | null,
    enabled = true
) =>
    useQuery({
        queryKey: programQuestionKeys.questions(country, userId, form, programId),
        enabled: enabled && Boolean(country),
        queryFn: async () => {
            const response = await api.programQuestions.questions({
                country,
                userId,
                form,
                programId,
            });
            return response.data;
        },
    });

export const useProgramQuestionsApplicable = (userId?: number) =>
    useQuery({
        queryKey: programQuestionKeys.applicable(userId),
        enabled: Boolean(userId && userId > 0),
        queryFn: async () => {
            const response = await api.programQuestions.applicable(userId);
            return response.data;
        },
    });

export const useSaveProgramQuestion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: SaveProgramQuestionRequest) => {
            const response = await api.programQuestions.put(payload);
            return response.data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['program-question', 'questions'],
            });
        },
    });
};
